import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Input, Button } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import styles from "./index.module.less";

export type ConfirmModalType = "confirm" | "warning" | "input";

export interface ConfirmModalProps {
    /** 是否显示 */
    open: boolean;
    /** 弹窗类型 */
    type?: ConfirmModalType;
    /** 标题 */
    title: string;
    /** 内容（confirm/warning 类型使用） */
    content?: string;
    /** 输入框配置（input 类型使用） */
    inputProps?: {
        value?: string;
        maxLength?: number;
        placeholder?: string;
    };
    /** 确认按钮文字 */
    okText?: string;
    /** 取消按钮文字 */
    cancelText?: string;
    /** 确认按钮加载状态 */
    confirmLoading?: boolean;
    /** 确认回调（input 类型会传入输入值） */
    onOk: (value?: string) => void | Promise<void>;
    /** 取消回调 */
    onCancel: () => void;
}

/**
 * 通用确认弹窗组件
 * 支持三种类型：confirm（普通确认）、warning（警告确认）、input（输入确认）
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
    open,
    type = "confirm",
    title,
    content,
    inputProps,
    okText = "确认",
    cancelText = "取消",
    confirmLoading = false,
    onOk,
    onCancel,
}) => {
    const [inputValue, setInputValue] = useState(inputProps?.value || "");

    // 同步外部传入的 value
    useEffect(() => {
        if (open && inputProps?.value !== undefined) {
            setInputValue(inputProps.value);
        }
    }, [open, inputProps?.value]);

    // 关闭时重置输入值
    useEffect(() => {
        if (!open) {
            setInputValue("");
        }
    }, [open]);

    const handleOk = () => {
        if (type === "input") {
            onOk(inputValue);
        } else {
            onOk();
        }
    };

    // 字数统计格式化函数
    const countFormatter = useCallback(
        ({ count, maxLength }: { count: number; maxLength?: number }) => {
            const max = maxLength ?? inputProps?.maxLength ?? 0;
            const isOverLimit = count >= max;
            const countColor = isOverLimit ? "#FD503E" : (count > 0 ? "#333" : "#B1B2BA");
            const slashColor = isOverLimit ? "#FD503E" : "#B1B2BA";
            return (
                <span>
                    <span style={{ color: countColor }}>{count}</span>
                    <span style={{ color: slashColor }}>/{max}</span>
                </span>
            );
        },
        [inputProps?.maxLength]
    );

    // 渲染标题（warning 类型带图标）
    const renderTitle = useMemo(() => {
        if (type === "warning") {
            return (
                <span className={styles.warningTitle}>
                    <ExclamationCircleFilled className={styles.warningIcon} />
                    {title}
                </span>
            );
        }
        return title;
    },[title, type]);

    // 渲染内容
    const renderContent = useMemo(() => {
        switch (type) {
            case "input": {
                // const maxLen = inputProps?.maxLength;
                return (
                    <div className={styles.inputContent}>
                        <Input
                            style={{height: 40, paddingLeft: 12, paddingRight: 12, borderRadius: 12}}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            // maxLength={maxLen}
                            placeholder={inputProps?.placeholder || "请输入"}
                            // showCount={maxLen ? { formatter: countFormatter } : false}
                            showCount={false}
                        />
                    </div>
                );
            }
            case "warning":
            case "confirm":
            default:
                return <div className={styles.confirmContent}>{content}</div>;
        }
    }, [content, inputProps?.placeholder, inputValue, type]);

    return (
        <Modal
            open={open}
            title={renderTitle}
            onCancel={onCancel}
            footer={null}
            centered
            className={styles.confirmModal}
            width={500}
            zIndex={1100}
        >
            {renderContent}
            <div className={styles.footer}>
                <Button className={styles.footerBtn} onClick={onCancel}>
                    {cancelText}
                </Button>
                <Button
                    className={styles.footerBtn}
                    type="primary"
                    loading={confirmLoading}
                    disabled={type === "input" && !inputValue.trim()}
                    onClick={handleOk}
                >
                    {okText}
                </Button>
            </div>
        </Modal>
    );
};

export default ConfirmModal;